import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import { graphqlClient } from "@/api/graphql";
import { traderSchema } from "@/api/types/digitalnisklady.cz/types";
import { entitySchema } from "@/common/schema/entity";
import { sortString } from "@/common/utils/array";
import { getBestPrices } from "@/common/utils/trading";
import { graphql } from "@/generated/digitalnisklady.cz";
import {
  CropPricesRegionQuery,
  Currency,
} from "@/generated/digitalnisklady.cz/graphql";

// eslint-disable-next-line lingui/no-unlocalized-strings
const cropPricesQueryDocument = graphql(`
  query CropPricesRegion(
    $latitude: Float!
    $longitude: Float!
    $currency: Currency!
    $cropId: ID
    $companyId: ID
    $locale: String
  ) {
    cropPricesInRegion(
      gps: { latitude: $latitude, longitude: $longitude }
      cropId: $cropId
      tradeType: FROM_STORAGE
      companyId: $companyId
    ) {
      trader {
        id
        name
        logo
        tocUrl
      }
      id
      crop {
        id
        name(locale: $locale)
      }
      quarter {
        id
        name
      }
      price(currency: $currency)
      date
    }
  }
`);

const priceSchema = z.object({
  date: z.number(),
  id: z.string(),
  price: z.number(),
  quarter: entitySchema,
  trader: traderSchema,
});

type PriceSchema = z.infer<typeof priceSchema>;

const cropPricesSchema = z.array(
  z.object({
    crop: z.object({
      id: z.string(),
      name: z.string(),
    }),
    prices: z.array(priceSchema),
  }),
);

type CropPricesSchema = z.infer<typeof cropPricesSchema>;

const cropPricesQuery = (variables: {
  latitude: number;
  longitude: number;
  currency: Currency;
  cropId: string;
  companyId: string | undefined;
  locale: string;
}) => {
  return queryOptions({
    queryFn: () => graphqlClient.request(cropPricesQueryDocument, variables),
    queryKey: ["crop-prices", variables],
  });
};

const orderCropPrices = (data: CropPricesRegionQuery) => {
  const result = data.cropPricesInRegion?.toSorted((a, b) => {
    const aName = a?.crop?.name;
    const bName = b?.crop?.name;

    if (aName && bName) {
      return sortString(aName, bName);
    }

    return 0;
  });

  return z.array(priceSchema).parse(result);
};

const selectCropPrices = (data: CropPricesRegionQuery) => {
  const result: z.infer<typeof cropPricesSchema> = [];
  data.cropPricesInRegion?.sort((a, b) => {
    const aName = a?.crop?.name;
    const bName = b?.crop?.name;

    if (aName && bName) {
      return sortString(aName, bName);
    }

    return 0;
  });

  data.cropPricesInRegion?.forEach((price) => {
    const index = result.findIndex((y) => y.crop.id === price?.crop?.id);
    const { crop, ...rest } = price ?? {};

    if (index === -1) {
      result.push({
        // @ts-expect-error checked with zod
        crop,
        // @ts-expect-error checked with zod
        prices: [rest],
      });
    } else {
      // @ts-expect-error checked with zod
      result[index].prices.push(rest);
    }
  });

  return cropPricesSchema.parse(result);
};

const selectBestCropPrices = (data: CropPricesSchema) => {
  const bestPrices = data.map((prices) => {
    return {
      crop: prices.crop,
      prices: getBestPrices(prices.prices),
    };
  });

  return cropPricesSchema.parse(bestPrices);
};

const selectPricesByQuarter = <T extends { quarter: { id: string } }>(
  data: Array<T>,
  quarterId: string,
) => {
  return data.filter((d) => d.quarter.id === quarterId);
};

export {
  cropPricesQuery,
  orderCropPrices,
  selectBestCropPrices,
  selectCropPrices,
  selectPricesByQuarter,
};
export type { CropPricesSchema, PriceSchema };
